import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';

const GalleryImage = ({ images, imageBaseUrl }) => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const resolved = (images || [])
    .map((img) => ({
      src: img.des_gallery ? `${imageBaseUrl}/${img.des_gallery}` : (img.url || null),
      caption: img.caption || '',
    }))
    .filter((img) => img.src);

  const openLightbox = (idx) => setLightboxIndex(idx);
  const closeLightbox = () => setLightboxIndex(null);

  const showPrev = useCallback(() => {
    setLightboxIndex((prev) => (prev === null ? null : (prev - 1 + resolved.length) % resolved.length));
  }, [resolved.length]);

  const showNext = useCallback(() => {
    setLightboxIndex((prev) => (prev === null ? null : (prev + 1) % resolved.length));
  }, [resolved.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;

    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex, showPrev, showNext]);

  if (resolved.length === 0) return null;

  return (
    <div className="pub-cus-des-gallery-wrapper">
      <div className="d-flex align-items-center gap-3 mb-4">
        <h3 className="fw-bold mb-0" style={{ color: '#1B4965', fontSize: '1.15rem' }}>
          Photo Gallery
        </h3>
        <div className="pub-cus-des-gallery-line" />
      </div>

      <div className="row g-3">
        {resolved.map((img, idx) => (
          <div key={idx} className="col-md-6 col-lg-4">
            <div
              className="pub-cus-des-gallery-item"
              onClick={() => openLightbox(idx)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && openLightbox(idx)}
            >
              <img
                src={img.src}
                alt={img.caption || `Gallery ${idx + 1}`}
                className="pub-cus-des-gallery-img"
                loading="lazy"
                onError={(e) => { e.target.closest('.pub-cus-des-gallery-item').style.display = 'none'; }}
              />
              <div className="pub-cus-des-gallery-overlay">
                {img.caption && (
                  <span className="pub-cus-des-gallery-caption">{img.caption}</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {lightboxIndex !== null && (
        <div className="pub-cus-des-lightbox-overlay" onClick={closeLightbox}>
          <button className="pub-cus-des-lightbox-close" onClick={closeLightbox} aria-label="Close">
            <X size={22} />
          </button>

          {resolved.length > 1 && (
            <button
              className="pub-cus-des-lightbox-nav pub-cus-des-lightbox-nav--prev"
              onClick={(e) => { e.stopPropagation(); showPrev(); }}
              aria-label="Previous image"
            >
              <ChevronLeft size={26} />
            </button>
          )}

          <div className="pub-cus-des-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={resolved[lightboxIndex].src}
              alt={resolved[lightboxIndex].caption || `Gallery ${lightboxIndex + 1}`}
              className="pub-cus-des-lightbox-img"
            />
            <div className="pub-cus-des-lightbox-meta">
              {resolved[lightboxIndex].caption && (
                <span className="pub-cus-des-lightbox-caption">{resolved[lightboxIndex].caption}</span>
              )}
              <span className="pub-cus-des-lightbox-counter">
                <ImageIcon size={13} /> {lightboxIndex + 1} / {resolved.length}
              </span>
            </div>
          </div>

          {resolved.length > 1 && (
            <button
              className="pub-cus-des-lightbox-nav pub-cus-des-lightbox-nav--next"
              onClick={(e) => { e.stopPropagation(); showNext(); }}
              aria-label="Next image"
            >
              <ChevronRight size={26} />
            </button>
          )}

          {resolved.length > 1 && (
            <div className="pub-cus-des-lightbox-thumbs" onClick={(e) => e.stopPropagation()}>
              {resolved.map((img, idx) => (
                <img
                  key={idx}
                  src={img.src}
                  alt=""
                  className={`pub-cus-des-lightbox-thumb ${idx === lightboxIndex ? 'active' : ''}`}
                  onClick={() => setLightboxIndex(idx)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GalleryImage;